import { useState } from 'react';

const ValueProposition = () => {
  const [activeCard, setActiveCard] = useState<number | null>(null);

  const values = [
    {
      id: 1,
      badge: '五大絕對變量',
      title: '精準輸入，摒棄冗長問卷',
      description: '摒棄傳統諮詢的冗長問卷。僅需輸入時間與地理錨點，即可啟動底層運算。五個絕對變量，構建您的完整行為基線。',
      example: {
        label: '輸入變量示例',
        text: 'DATE × TIME × GEO_LAT × GEO_LNG × ROLE_VECTOR → BASELINE_PARAMS'
      },
      icon: 'ri-map-pin-2-line',
      color: 'emerald'
    },
    {
      id: 2,
      badge: '多模型投票共振',
      title: '東西方演算法集成學習',
      description: 'Deno 引擎同步解析東方干支系統與西方靈數學，透過集成學習投票機制，尋找時空概率的絕對收斂點，消除單一模型的系統性偏差。',
      example: {
        label: '共振演算邏輯',
        text: '干支模型 × 靈數模型 × 行為經濟學 → 時空概率收斂點'
      },
      icon: 'ri-git-merge-line',
      color: 'cyan'
    },
    {
      id: 3,
      badge: '麥肯錫級戰略交付',
      title: 'AI 結構化轉譯，零模糊地帶',
      description: 'GPT-4o 作為純粹的結構化編譯器，將 Deno 確定性核心的硬編碼參數，自動生成包含 6 大矩陣、12 個月高頻預警圖的 PDF 戰略報告。',
      example: {
        label: '交付規格',
        text: '6 大戰略矩陣 + 12 個月預警圖 + 流動性防禦協議 → PDF'
      },
      icon: 'ri-file-chart-line',
      color: 'amber'
    }
  ];

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'emerald':
        return {
          border: 'border-[#00ffb4]/20',
          bg: 'bg-[#00ffb4]/5',
          text: 'text-[#00ffb4]',
          badge: 'bg-[#00ffb4]/10 text-[#00ffb4] border border-[#00ffb4]/20',
        };
      case 'cyan':
        return {
          border: 'border-[#00d4ff]/20',
          bg: 'bg-[#00d4ff]/5',
          text: 'text-[#00d4ff]',
          badge: 'bg-[#00d4ff]/10 text-[#00d4ff] border border-[#00d4ff]/20',
        };
      case 'amber':
        return {
          border: 'border-amber-400/20',
          bg: 'bg-amber-400/5',
          text: 'text-amber-400',
          badge: 'bg-amber-400/10 text-amber-400 border border-amber-400/20',
        };
      default:
        return {
          border: 'border-gray-700',
          bg: 'bg-gray-800/50',
          text: 'text-gray-400',
          badge: 'bg-gray-700 text-gray-300',
        };
    }
  };

  return (
    <section className="relative bg-[#050508] py-32 overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(0,255,180,0.03) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(0,255,180,0.03) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }}></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#00ffb4]/20 bg-[#00ffb4]/5 rounded-full mb-6">
            <i className="ri-cpu-line text-[#00ffb4] text-sm"></i>
            <span className="text-xs font-mono text-[#00ffb4] tracking-wider">CORE FEATURES</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            三大核心能力
          </h2>
          <p className="text-lg text-gray-500 max-w-3xl mx-auto leading-relaxed">
            我們不販售安慰劑。我們販售演算精度。
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {values.map((value) => {
            const colors = getColorClasses(value.color);
            const isActive = activeCard === value.id;

            return (
              <div
                key={value.id}
                onMouseEnter={() => setActiveCard(value.id)}
                onMouseLeave={() => setActiveCard(null)}
                className={`relative bg-[#0a0a0f] border ${colors.border} rounded-xl p-8 transition-all duration-500 cursor-pointer ${
                  isActive ? 'scale-105' : ''
                }`}
              >
                <div className={`inline-flex items-center gap-2 px-3 py-1 ${colors.badge} rounded-full mb-6`}>
                  <span className="text-xs font-mono tracking-wider">{value.badge}</span>
                </div>

                <div className={`w-14 h-14 flex items-center justify-center ${colors.bg} border ${colors.border} rounded-xl mb-6 transition-transform duration-500 ${
                  isActive ? 'scale-110' : ''
                }`}>
                  <i className={`${value.icon} ${colors.text} text-2xl`}></i>
                </div>

                <h3 className="text-xl font-bold text-white mb-4">{value.title}</h3>
                <p className="text-gray-500 leading-relaxed mb-6 text-sm">{value.description}</p>

                <div className={`${colors.bg} border ${colors.border} rounded-lg p-4`}>
                  <div className="flex items-center gap-2 mb-2">
                    <i className={`ri-terminal-box-line ${colors.text} text-xs`}></i>
                    <span className={`text-xs font-mono ${colors.text} tracking-wider`}>
                      {value.example.label}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 font-mono leading-relaxed">
                    {value.example.text}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-[#0a0a0f] border border-[#00ffb4]/15 rounded-xl p-6 text-center">
            <div className="text-4xl font-bold mb-2" style={{ color: '#00ffb4' }}>6 大</div>
            <div className="text-sm text-gray-500 font-mono">戰略矩陣模組</div>
          </div>
          <div className="bg-[#0a0a0f] border border-[#00d4ff]/15 rounded-xl p-6 text-center">
            <div className="text-4xl font-bold mb-2" style={{ color: '#00d4ff' }}>&lt;5 分鐘</div>
            <div className="text-sm text-gray-500 font-mono">自動化交付時效</div>
          </div>
          <div className="bg-[#0a0a0f] border border-amber-400/15 rounded-xl p-6 text-center">
            <div className="text-4xl font-bold text-amber-400 mb-2">100%</div>
            <div className="text-sm text-gray-500 font-mono">確定性核心演算</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ValueProposition;
